import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Send,
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  BarChart3,
  Users,
  CheckCircle,
  Clock,
  Calendar,
  MessageSquare,
  AlertTriangle,
  Eye,
  Search,
  RefreshCw,
  Loader2,
  XCircle
} from 'lucide-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { campaignAPI } from '../../api';
import { useFormat } from '../../i18n/format.js';
import { handleApiError } from '../../utils/errorHandler';
import CampaignFormModal from './forms/CampaignFormModal';
import CampaignDetailModal from './modals/CampaignDetailModal';
import CampaignResponsesModal from './modals/CampaignResponsesModal';

// Campaign type configuration — labelKey resolved at render (never a translated string
// baked into a module-level constant, it would freeze at the language active on load).
const CAMPAIGN_TYPE_CONFIG = {
  information: {
    labelKey: 'campaigns.type.information',
    icon: MessageSquare,
    color: 'blue',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-300'
  },
  survey: {
    labelKey: 'campaigns.type.survey',
    icon: BarChart3,
    color: 'purple',
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-300'
  },
  safety_reminder: {
    labelKey: 'campaigns.type.safety_reminder',
    icon: AlertTriangle,
    color: 'orange',
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300'
  },
  document_request: {
    labelKey: 'campaigns.type.document_request',
    icon: Send,
    color: 'indigo',
    bg: 'bg-indigo-100',
    text: 'text-indigo-800',
    border: 'border-indigo-300'
  }
};

// Campaign status configuration — same rule, labelKey only.
const CAMPAIGN_STATUS_CONFIG = {
  draft: {
    labelKey: 'campaigns.status.draft',
    icon: Edit,
    color: 'gray',
    bg: 'bg-gray-100',
    text: 'text-gray-800'
  },
  active: {
    labelKey: 'campaigns.status.active',
    icon: CheckCircle,
    color: 'green',
    bg: 'bg-green-100',
    text: 'text-green-800'
  },
  paused: {
    labelKey: 'campaigns.status.paused',
    icon: Pause,
    color: 'yellow',
    bg: 'bg-yellow-100',
    text: 'text-yellow-800'
  },
  completed: {
    labelKey: 'campaigns.status.completed',
    icon: CheckCircle,
    color: 'blue',
    bg: 'bg-blue-100',
    text: 'text-blue-800'
  },
  cancelled: {
    labelKey: 'campaigns.status.cancelled',
    icon: XCircle,
    color: 'red',
    bg: 'bg-red-100',
    text: 'text-red-800'
  }
};

// Schedule type keys — resolved via campaigns.schedule.<code> at render.
const SCHEDULE_TYPE_KEYS = {
  immediate: 'campaigns.schedule.immediate',
  scheduled: 'campaigns.schedule.scheduled',
  recurring: 'campaigns.schedule.recurring'
};

function CampaignsView() {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showResponsesModal, setShowResponsesModal] = useState(false);

  const queryClient = useQueryClient();

  // Fetch campaigns
  const { data: campaignsData, isLoading, error, refetch } = useQuery({
    queryKey: ['campaigns', filterStatus, filterType],
    queryFn: () => campaignAPI.getAll({ status: filterStatus, campaign_type: filterType }),
    select: (data) => data?.data || []
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => campaignAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
    },
    onError: (error) => {
      handleApiError(error, t('campaigns.errors.delete'));
    }
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: (id) => campaignAPI.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
    },
    onError: (error) => {
      handleApiError(error, t('campaigns.errors.activate'));
    }
  });

  // Pause mutation
  const pauseMutation = useMutation({
    mutationFn: (id) => campaignAPI.pause(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
    },
    onError: (error) => {
      handleApiError(error, t('campaigns.errors.pause'));
    }
  });

  // Filtered campaigns
  const filteredCampaigns = useMemo(() => {
    if (!campaignsData) return [];

    return campaignsData.filter(campaign => {
      const matchesSearch = !searchTerm ||
        campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  }, [campaignsData, searchTerm]);

  // Statistics
  const stats = useMemo(() => {
    if (!campaignsData) return { total: 0, active: 0, draft: 0, completed: 0 };

    return {
      total: campaignsData.length,
      active: campaignsData.filter(c => c.status === 'active').length,
      draft: campaignsData.filter(c => c.status === 'draft').length,
      completed: campaignsData.filter(c => c.status === 'completed').length
    };
  }, [campaignsData]);

  const handleEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setShowFormModal(true);
  };

  const handleView = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
  };

  const handleViewResponses = (campaign) => {
    setSelectedCampaign(campaign);
    setShowResponsesModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm(t('campaigns.confirm.delete'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleActivate = (id) => {
    if (window.confirm(t('campaigns.confirm.activate'))) {
      activateMutation.mutate(id);
    }
  };

  const handlePause = (id) => {
    if (window.confirm(t('campaigns.confirm.pause'))) {
      pauseMutation.mutate(id);
    }
  };

  // Calculate delivery rate
  const getDeliveryRate = (campaign) => {
    if (!campaign.target_count || campaign.target_count === 0) return '0%';
    const rate = (campaign.sent_count / campaign.target_count * 100).toFixed(1);
    return `${rate}%`;
  };

  // Calculate read rate
  const getReadRate = (campaign) => {
    if (!campaign.sent_count || campaign.sent_count === 0) return '0%';
    const rate = (campaign.read_count / campaign.sent_count * 100).toFixed(1);
    return `${rate}%`;
  };

  // Calculate response rate
  const getResponseRate = (campaign) => {
    if (!campaign.sent_count || campaign.sent_count === 0) return '0%';
    const rate = (campaign.response_count / campaign.sent_count * 100).toFixed(1);
    return `${rate}%`;
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">{t('common.states.loadError')}</h3>
          <p className="text-red-600 text-sm mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header Card with Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('campaigns.title')}</h1>
              <p className="text-sm text-gray-500">
                {t('campaigns.stats.count', { count: stats.total })} • {t('campaigns.stats.activeCount', { count: stats.active })} • {t('campaigns.stats.draftCount', { count: stats.draft })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => {
                setSelectedCampaign(null);
                setShowFormModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('campaigns.newCampaign')}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('campaigns.search.placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            )}
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('campaigns.filters.allStatuses')}</option>
            {Object.entries(CAMPAIGN_STATUS_CONFIG).map(([code, config]) => (
              <option key={code} value={code}>{t(config.labelKey)}</option>
            ))}
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('campaigns.filters.allTypes')}</option>
            {Object.entries(CAMPAIGN_TYPE_CONFIG).map(([code, config]) => (
              <option key={code} value={code}>{t(config.labelKey)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Campaigns List */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Send className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('campaigns.empty.title')}</h3>
          <p className="text-gray-500 mb-4">
            {t('campaigns.empty.description')}
          </p>
          <button
            onClick={() => {
              setSelectedCampaign(null);
              setShowFormModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('campaigns.empty.action')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCampaigns.map((campaign) => {
            const typeConfig = CAMPAIGN_TYPE_CONFIG[campaign.campaign_type] || CAMPAIGN_TYPE_CONFIG.information;
            const statusConfig = CAMPAIGN_STATUS_CONFIG[campaign.status] || CAMPAIGN_STATUS_CONFIG.draft;
            const TypeIcon = typeConfig.icon;
            const StatusIcon = statusConfig.icon;

            return (
              <div
                key={campaign.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`${typeConfig.bg} p-2 rounded-lg`}>
                        <TypeIcon className={`w-5 h-5 ${typeConfig.text}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                        <p className="text-sm text-gray-500">{campaign.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 mt-3">
                      {/* Status Badge */}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {t(statusConfig.labelKey)}
                      </span>

                      {/* Type Badge */}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig.bg} ${typeConfig.text}`}>
                        {t(typeConfig.labelKey)}
                      </span>

                      {/* Schedule */}
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {SCHEDULE_TYPE_KEYS[campaign.schedule_type] ? t(SCHEDULE_TYPE_KEYS[campaign.schedule_type]) : campaign.schedule_type}
                      </span>

                      {/* Priority */}
                      {campaign.message_priority && campaign.message_priority !== 'normal' && (
                        <span className={`text-sm font-medium ${
                          campaign.message_priority === 'urgent' ? 'text-red-600' :
                          campaign.message_priority === 'high' ? 'text-orange-600' :
                          'text-gray-600'
                        }`}>
                          {t('campaigns.card.priority', { priority: campaign.message_priority })}
                        </span>
                      )}
                    </div>

                    {/* Statistics */}
                    <div className="flex items-center space-x-6 mt-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        <span>{t('campaigns.card.targeted', { count: campaign.target_count || 0 })}</span>
                      </div>
                      <div className="flex items-center">
                        <Send className="w-4 h-4 mr-1" />
                        <span>{t('campaigns.card.sent', { count: campaign.sent_count || 0, rate: getDeliveryRate(campaign) })}</span>
                      </div>
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        <span>{t('campaigns.card.read', { count: campaign.read_count || 0, rate: getReadRate(campaign) })}</span>
                      </div>
                      {campaign.requires_response && (
                        <div className="flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          <span>{t('campaigns.card.responses', { count: campaign.response_count || 0, rate: getResponseRate(campaign) })}</span>
                        </div>
                      )}
                    </div>

                    {/* Dates */}
                    <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                      {campaign.created_at && (
                        <span>{t('campaigns.card.createdOn', { date: fmt.date(campaign.created_at) })}</span>
                      )}
                      {campaign.last_sent_at && (
                        <span>{t('campaigns.card.lastSentOn', { date: fmt.date(campaign.last_sent_at) })}</span>
                      )}
                      {campaign.scheduled_at && campaign.status !== 'completed' && (
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {t('campaigns.card.scheduledFor', { date: fmt.date(campaign.scheduled_at) })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col space-y-2 ml-4">
                    <button
                      onClick={() => handleView(campaign)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <BarChart3 className="w-3 h-3 mr-1" />
                      {t('campaigns.actions.stats')}
                    </button>

                    {campaign.status === 'draft' && (
                      <button
                        onClick={() => handleActivate(campaign.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                      >
                        <Play className="w-3 h-3 mr-1" />
                        {t('campaigns.actions.activate')}
                      </button>
                    )}

                    {campaign.status === 'active' && (
                      <button
                        onClick={() => handlePause(campaign.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-yellow-600 hover:bg-yellow-700"
                      >
                        <Pause className="w-3 h-3 mr-1" />
                        {t('campaigns.actions.pause')}
                      </button>
                    )}

                    {campaign.status === 'paused' && (
                      <button
                        onClick={() => handleActivate(campaign.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                      >
                        <Play className="w-3 h-3 mr-1" />
                        {t('campaigns.actions.resume')}
                      </button>
                    )}

                    {(campaign.status === 'draft' || campaign.status === 'paused') && (
                      <button
                        onClick={() => handleEdit(campaign)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        {t('common.actions.edit')}
                      </button>
                    )}

                    {campaign.sent_count > 0 && (
                      <button
                        onClick={() => handleViewResponses(campaign)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Users className="w-3 h-3 mr-1" />
                        {t('campaigns.actions.responses')}
                      </button>
                    )}

                    {campaign.status === 'draft' && (
                      <button
                        onClick={() => handleDelete(campaign.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        {t('common.actions.delete')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showFormModal && (
        <CampaignFormModal
          campaign={selectedCampaign}
          onClose={() => {
            setShowFormModal(false);
            setSelectedCampaign(null);
          }}
          onSuccess={() => {
            setShowFormModal(false);
            setSelectedCampaign(null);
            refetch();
          }}
        />
      )}

      {showDetailModal && selectedCampaign && (
        <CampaignDetailModal
          campaign={selectedCampaign}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCampaign(null);
          }}
        />
      )}

      {showResponsesModal && selectedCampaign && (
        <CampaignResponsesModal
          campaign={selectedCampaign}
          onClose={() => {
            setShowResponsesModal(false);
            setSelectedCampaign(null);
          }}
        />
      )}
    </div>
  );
}

export default CampaignsView;
